import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'
import { useProjectStore } from 'Store/Project'
import { shallow } from 'zustand/shallow'
import { JobClosingDocument, JobClosingDocumentInput } from './types'
import {
  apiDetailToDocument,
  apiListItemToSummary,
  formInputToApiBody,
} from './apiMapper'

interface JobClosingContextValue {
  jobClosingDocuments: JobClosingDocument[]
  isLoading: boolean
  isSubmitting: boolean
  refreshDocuments: () => Promise<void>
  getDocument: (id: string) => Promise<JobClosingDocument>
  createDocument: (input: JobClosingDocumentInput, issue?: boolean) => Promise<JobClosingDocument>
  updateDocument: (id: string, input: JobClosingDocumentInput) => Promise<JobClosingDocument>
  issueDocument: (id: string) => Promise<JobClosingDocument>
  uploadSignedDocument: (id: string, file: File, signedByName: string) => Promise<JobClosingDocument>
}

const JobClosingContext = createContext<JobClosingContextValue | null>(null)

const assertSuccess = (success: boolean, data: any) => {
  if (!success) {
    throw new Error(typeof data === 'string' ? data : 'Request failed')
  }
}

export function JobClosingProvider({
  projectId,
  children,
}: {
  projectId: string
  children: ReactNode
}) {
  const {
    getJobClosingList,
    getJobClosing,
    createJobClosing,
    updateJobClosing,
    issueJobClosing,
    uploadJobClosingSignedFile,
  } = useProjectStore(
    state => ({
      getJobClosingList: state.getJobClosingList,
      getJobClosing: state.getJobClosing,
      createJobClosing: state.createJobClosing,
      updateJobClosing: state.updateJobClosing,
      issueJobClosing: state.issueJobClosing,
      uploadJobClosingSignedFile: state.uploadJobClosingSignedFile,
    }),
    shallow
  )

  const [jobClosingDocuments, setJobClosingDocuments] = useState<JobClosingDocument[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const projectIdNum = parseInt(projectId, 10)

  const refreshDocuments = useCallback(async () => {
    if (!projectId) return
    setIsLoading(true)
    try {
      const { success, data } = await getJobClosingList(projectIdNum)
      assertSuccess(success, data)
      const list = Array.isArray(data) ? data.map(apiListItemToSummary) : []
      setJobClosingDocuments(list)
    } finally {
      setIsLoading(false)
    }
  }, [getJobClosingList, projectId, projectIdNum])

  useEffect(() => {
    refreshDocuments()
  }, [refreshDocuments])

  const getDocument = useCallback(
    async (id: string) => {
      const { success, data } = await getJobClosing(projectIdNum, id)
      assertSuccess(success, data)
      return apiDetailToDocument(data)
    },
    [getJobClosing, projectIdNum]
  )

  const createDocument = useCallback(
    async (input: JobClosingDocumentInput, issue = false) => {
      setIsSubmitting(true)
      try {
        const body = formInputToApiBody(input)
        const { success, data } = await createJobClosing(projectIdNum, body)
        assertSuccess(success, data)

        const created = apiDetailToDocument(data)
        const docId = created.id

        if (issue) {
          const issueRes = await issueJobClosing(projectIdNum, docId)
          assertSuccess(issueRes.success, issueRes.data)
        }

        await refreshDocuments()
        return getDocument(docId)
      } finally {
        setIsSubmitting(false)
      }
    },
    [createJobClosing, getDocument, issueJobClosing, projectIdNum, refreshDocuments]
  )

  const updateDocument = useCallback(
    async (id: string, input: JobClosingDocumentInput) => {
      setIsSubmitting(true)
      try {
        const body = formInputToApiBody(input)
        const { success, data } = await updateJobClosing(projectIdNum, id, body)
        assertSuccess(success, data)
        await refreshDocuments()
        return getDocument(id)
      } finally {
        setIsSubmitting(false)
      }
    },
    [getDocument, projectIdNum, refreshDocuments, updateJobClosing]
  )

  const issueDocument = useCallback(
    async (id: string) => {
      setIsSubmitting(true)
      try {
        const { success, data } = await issueJobClosing(projectIdNum, id)
        assertSuccess(success, data)
        await refreshDocuments()
        return getDocument(id)
      } finally {
        setIsSubmitting(false)
      }
    },
    [getDocument, issueJobClosing, projectIdNum, refreshDocuments]
  )

  const uploadSignedDocument = useCallback(
    async (id: string, file: File, signedByName: string) => {
      setIsSubmitting(true)
      try {
        const { success, data } = await uploadJobClosingSignedFile(
          projectIdNum,
          id,
          file,
          signedByName
        )
        assertSuccess(success, data)
        await refreshDocuments()
        return getDocument(id)
      } finally {
        setIsSubmitting(false)
      }
    },
    [getDocument, projectIdNum, refreshDocuments, uploadJobClosingSignedFile]
  )

  return (
    <JobClosingContext.Provider
      value={{
        jobClosingDocuments,
        isLoading,
        isSubmitting,
        refreshDocuments,
        getDocument,
        createDocument,
        updateDocument,
        issueDocument,
        uploadSignedDocument,
      }}>
      {children}
    </JobClosingContext.Provider>
  )
}

export function useJobClosingContext() {
  const ctx = useContext(JobClosingContext)
  if (!ctx) {
    throw new Error('useJobClosingContext must be used within JobClosingProvider')
  }
  return ctx
}
